import { computed, inject, Injectable, Signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { getFeatures } from '@fc-store/selectors/features.selectors';
import { FeatureKey } from './feature.model';

/**
 * localStorage key holding dev feature overrides as a
 * `{ [key]: boolean }` map. A key absent from the map follows the backend
 * customer feature. Read once per service lifetime — reload the page after
 * toggling (the developer-options dialog does this on save).
 */
export const FEATURE_OVERRIDES_STORAGE_KEY = 'fc.featureOverrides';

export type FeatureOverrides = Partial<Record<FeatureKey, boolean>>;

@Injectable({ providedIn: 'root' })
export class FeatureFlagService {
  private ngrxStore = inject(Store);
  private features = this.ngrxStore.selectSignal(getFeatures);
  private overrides = readOverrides();
  private enabledByKey = new Map<FeatureKey, Signal<boolean>>();

  /**
   * The backend feature list with dev overrides applied; null until the
   * backend features load. Use this instead of the raw `getFeatures`
   * selector when filtering against the whole list (e.g. navigation).
   */
  readonly effectiveFeatures: Signal<FeatureKey[] | null> = computed(() => {
    const backendFeatures = this.features();
    if (backendFeatures === null) return null;
    const effective = new Set(backendFeatures);
    for (const [key, enabled] of Object.entries(this.overrides)) {
      if (enabled) effective.add(key as FeatureKey);
      else effective.delete(key as FeatureKey);
    }
    return [...effective];
  });

  /** What the backend customer feature alone decides, ignoring dev overrides. */
  isEnabledByFeature(key: FeatureKey): boolean {
    return this.features()?.includes(key) ?? false;
  }

  /** Effective state: the local dev override when set, the backend flag otherwise. */
  enabled(key: FeatureKey): Signal<boolean> {
    let enabled = this.enabledByKey.get(key);
    if (!enabled) {
      enabled = computed(
        () => this.overrides[key] ?? this.isEnabledByFeature(key),
      );
      this.enabledByKey.set(key, enabled);
    }
    return enabled;
  }

  overrideFor(key: FeatureKey): boolean | undefined {
    return this.overrides[key];
  }

  /**
   * Persists the dev overrides map; omit a key to leave that flag following
   * the backend feature. Takes effect on the next page load — overrides are
   * read once per service lifetime.
   */
  writeOverrides(overrides: FeatureOverrides): void {
    try {
      if (Object.keys(overrides).length) {
        localStorage.setItem(
          FEATURE_OVERRIDES_STORAGE_KEY,
          JSON.stringify(overrides),
        );
      } else {
        localStorage.removeItem(FEATURE_OVERRIDES_STORAGE_KEY);
      }
    } catch {
      // Storage unavailable (private mode quota etc.) — overrides are a dev
      // convenience, so failing silently is fine.
    }
  }
}

function readOverrides(): FeatureOverrides {
  try {
    const stored = localStorage.getItem(FEATURE_OVERRIDES_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}
